import { buildSpeakersTreeNode } from './speakers-tree-node';
export class SpeakersTree {
    constructor(nodes, focus) {
        this.nodes = nodes;
        this.focus = focus;
    }
    static fromSpeakers(speakers, pinnedSpeakers = [], expandAll = false) {
        const pinnedDbIds = new Set(pinnedSpeakers.map((s) => s.dbid).filter(Boolean));
        const filteredSpeakers = speakers.filter((s) => s.dbid === null || !pinnedDbIds.has(s.dbid));
        const buildNode = (s, isPinned) => {
            const data = buildSpeakersTreeNode(s);
            return Object.assign(Object.assign({}, data), { isExpanded: expandAll && data.rolesCount > 0, isPinned });
        };
        const nodes = [
            ...pinnedSpeakers.map((s) => buildNode(s, true)),
            ...filteredSpeakers.map((s) => buildNode(s, false)),
        ];
        const focus = nodes.length > 0
            ? { kind: 'speaker', speakerIndex: 0 }
            : { kind: 'addSpeaker' };
        return new SpeakersTree(nodes, focus);
    }
    static empty() {
        return SpeakersTree.fromSpeakers([]);
    }
    moveFocusDown() {
        return new SpeakersTree(this.nodes, this.nextCursor(this.focus));
    }
    moveFocusUp() {
        return new SpeakersTree(this.nodes, this.prevCursor(this.focus));
    }
    expandFocused(allowButtonActions) {
        const { focus } = this;
        if (focus.kind !== 'speaker') {
            return this;
        }
        const node = this.nodes[focus.speakerIndex];
        const canExpand = node.rolesCount > 0 || allowButtonActions;
        if (!canExpand) {
            return this;
        }
        if (!node.isExpanded) {
            return this.toggleExpand(focus.speakerIndex);
        }
        const firstChild = node.rolesCount > 0
            ? { kind: 'role', speakerIndex: focus.speakerIndex, roleIndex: 0 }
            : { kind: 'noRole', speakerIndex: focus.speakerIndex };
        return new SpeakersTree(this.nodes, firstChild);
    }
    collapseFocused() {
        const { focus } = this;
        if (focus.kind === 'speaker') {
            const node = this.nodes[focus.speakerIndex];
            if (node.isExpanded) {
                return this.toggleExpand(focus.speakerIndex);
            }
            return this;
        }
        if (focus.kind === 'role' || focus.kind === 'noRole' || focus.kind === 'addRole' || focus.kind === 'editSpeaker') {
            return new SpeakersTree(this.nodes, { kind: 'speaker', speakerIndex: focus.speakerIndex });
        }
        return this;
    }
    toggleExpand(speakerIndex) {
        var _a;
        const nodes = this.nodes.map((node, i) => (i === speakerIndex ? Object.assign(Object.assign({}, node), { isExpanded: !node.isExpanded }) : node));
        const isCollapsing = ((_a = this.nodes[speakerIndex]) === null || _a === void 0 ? void 0 : _a.isExpanded) === true;
        if (isCollapsing) {
            const { focus } = this;
            const isOnChild = (focus.kind === 'role' || focus.kind === 'noRole' || focus.kind === 'addRole' || focus.kind === 'editSpeaker')
                && focus.speakerIndex === speakerIndex;
            if (isOnChild) {
                return new SpeakersTree(nodes, { kind: 'speaker', speakerIndex });
            }
        }
        return new SpeakersTree(nodes, this.focus);
    }
    getAction() {
        var _a, _b, _c;
        const { focus } = this;
        if (focus.kind === 'addSpeaker') {
            return { kind: 'addSpeaker' };
        }
        if (focus.kind === 'addRole') {
            const { speaker } = this.nodes[focus.speakerIndex];
            return {
                kind: 'addRole',
                speakerId: (_a = speaker.dbid) !== null && _a !== void 0 ? _a : speaker.id,
                firstName: speaker.firstName,
                lastName: speaker.lastName,
            };
        }
        if (focus.kind === 'editSpeaker') {
            const { speaker } = this.nodes[focus.speakerIndex];
            return {
                kind: 'editSpeaker',
                speakerId: speaker.id,
                firstName: speaker.firstName,
                lastName: speaker.lastName,
            };
        }
        const node = this.nodes[focus.speakerIndex];
        if (focus.kind === 'speaker') {
            return {
                kind: 'selectSpeaker',
                selection: { speaker: node.speaker, roleIndex: (_c = (_b = node.defaultRole) === null || _b === void 0 ? void 0 : _b.index) !== null && _c !== void 0 ? _c : 'none' },
            };
        }
        if (focus.kind === 'noRole') {
            return {
                kind: 'selectSpeaker',
                selection: { speaker: node.speaker, roleIndex: 'none' },
            };
        }
        return {
            kind: 'selectSpeaker',
            selection: {
                speaker: node.speaker,
                roleIndex: focus.roleIndex,
            },
        };
    }
    nextCursor(focus) {
        if (focus.kind === 'addSpeaker') {
            return focus;
        }
        if (focus.kind === 'editSpeaker') {
            const nextIndex = focus.speakerIndex + 1;
            return nextIndex < this.nodes.length
                ? { kind: 'speaker', speakerIndex: nextIndex }
                : { kind: 'addSpeaker' };
        }
        if (focus.kind === 'addRole') {
            return { kind: 'editSpeaker', speakerIndex: focus.speakerIndex };
        }
        if (focus.kind === 'noRole') {
            const node = this.nodes[focus.speakerIndex];
            return node.speaker.savedInDb === true
                ? { kind: 'addRole', speakerIndex: focus.speakerIndex }
                : { kind: 'editSpeaker', speakerIndex: focus.speakerIndex };
        }
        if (focus.kind === 'role') {
            const node = this.nodes[focus.speakerIndex];
            return focus.roleIndex < node.rolesCount - 1
                ? { kind: 'role', speakerIndex: focus.speakerIndex, roleIndex: focus.roleIndex + 1 }
                : { kind: 'noRole', speakerIndex: focus.speakerIndex };
        }
        const node = this.nodes[focus.speakerIndex];
        if (node.isExpanded) {
            return node.rolesCount > 0
                ? { kind: 'role', speakerIndex: focus.speakerIndex, roleIndex: 0 }
                : { kind: 'noRole', speakerIndex: focus.speakerIndex };
        }
        const nextIndex = focus.speakerIndex + 1;
        return nextIndex < this.nodes.length
            ? { kind: 'speaker', speakerIndex: nextIndex }
            : { kind: 'addSpeaker' };
    }
    prevCursor(focus) {
        if (focus.kind === 'addSpeaker') {
            if (this.nodes.length === 0) {
                return focus;
            }
            const lastIndex = this.nodes.length - 1;
            return this.nodes[lastIndex].isExpanded
                ? { kind: 'editSpeaker', speakerIndex: lastIndex }
                : { kind: 'speaker', speakerIndex: lastIndex };
        }
        if (focus.kind === 'editSpeaker') {
            const node = this.nodes[focus.speakerIndex];
            return node.speaker.savedInDb === true
                ? { kind: 'addRole', speakerIndex: focus.speakerIndex }
                : { kind: 'noRole', speakerIndex: focus.speakerIndex };
        }
        if (focus.kind === 'addRole') {
            return { kind: 'noRole', speakerIndex: focus.speakerIndex };
        }
        if (focus.kind === 'noRole') {
            const node = this.nodes[focus.speakerIndex];
            return node.rolesCount > 0
                ? { kind: 'role', speakerIndex: focus.speakerIndex, roleIndex: node.rolesCount - 1 }
                : { kind: 'speaker', speakerIndex: focus.speakerIndex };
        }
        if (focus.kind === 'role') {
            return focus.roleIndex > 0
                ? { kind: 'role', speakerIndex: focus.speakerIndex, roleIndex: focus.roleIndex - 1 }
                : { kind: 'speaker', speakerIndex: focus.speakerIndex };
        }
        if (focus.speakerIndex === 0) {
            return focus;
        }
        const prevIndex = focus.speakerIndex - 1;
        return this.nodes[prevIndex].isExpanded
            ? { kind: 'editSpeaker', speakerIndex: prevIndex }
            : { kind: 'speaker', speakerIndex: prevIndex };
    }
}
